import React, { useCallback, useMemo } from 'react';
import Image from 'next/legacy/image';
import { Box, Typography } from '@mui/material';
import type { WeatherConditions, WeatherDetail } from 'types/weather';
import { formatTimestampInTimezone } from 'utils/time';

import styles from './WeatherGraph.module.scss';

interface WeaterItemProps {
  condition: WeatherConditions | null;
  formattedDate: string | null;
  temperature: number | null;
  typographyVariant: 'callout1' | 'subHeadline';
  weatherIconPath: string;
}

const WeatherItem = ({
  condition,
  formattedDate,
  temperature,
  typographyVariant,
  weatherIconPath,
}: WeaterItemProps) => (
  <div className={styles.content} data-testid="weather-cell">
    <Typography className={styles.date} variant="caption2" color="textSecondary">
      {formattedDate}
    </Typography>
    {condition && (
      <Image
        alt={condition}
        className={styles.condition}
        height={20}
        src={`${weatherIconPath}/${condition}.svg`}
        width={20}
      />
    )}
    {temperature && (
      <div className={styles.temperature}>
        <Typography variant={typographyVariant} component="span">
          {Math.round(temperature)}º
        </Typography>
      </div>
    )}
  </div>
);

interface WeatherGraphProps {
  data: void[] | WeatherDetail[] | undefined;
  dateFormat: string;
  isMobile: boolean;
  weatherIconPath: string;
}

const WeatherGraph = ({ weatherIconPath, data, isMobile, dateFormat }: WeatherGraphProps) => {
  const typographyVariant = isMobile ? 'callout1' : 'subHeadline';

  const formattedDate = useCallback(
    (index: number) => {
      const hour = data?.[index];
      if (!hour) return null;
      return formatTimestampInTimezone(hour.timestamp, hour.utcOffset, dateFormat);
    },
    [dateFormat, data],
  );

  const WeatherGraphs = useMemo(
    () =>
      data?.map((weatherRecord: WeatherDetail | void, index: number) => (
        <WeatherItem
          condition={weatherRecord?.condition ?? null}
          formattedDate={formattedDate(index)}
          key={`weather-${weatherRecord?.timestamp}`}
          temperature={weatherRecord?.temperature ?? null}
          typographyVariant={typographyVariant}
          weatherIconPath={weatherIconPath}
        />
      )),
    [data, formattedDate, typographyVariant, weatherIconPath],
  );

  return <Box className={styles.weatherGraph}>{WeatherGraphs}</Box>;
};

export default WeatherGraph;
